/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */
import {Injectable} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';

import {Observable} from 'rxjs';
import {map, share} from 'rxjs/operators';

import {AiGenerateApiResponse, ETemplate, FieldAssociation, ResolvedPlaceholdersResponse} from './template-management.interfaces';

import {SelectedTreeItem} from '../+reports/reports.interfaces';
import {REQUEST_OPTIONS} from '../app.constants';
import {ApiService} from '../shared/api';
import {AuthenticationService} from '../shared/authentication';
import {TreeItem} from '../shared/ui/tree-multi-select/treeview.component';
import {TreeNode} from '../shared/ui/tree-select/tree-select.component';

export const templates_per_page = 15;

@Injectable()
export class TemplateManagementService {
  public serviceUrl = '/repository/api/v1/regulation/e-template/';
  public getToken$: Observable<{ Authorization: string }[]>;

  constructor(private api: ApiService,
              private authenticationService: AuthenticationService,
              private translate: TranslateService) {
    this.getToken$ = this.getToken();
  }

  public getSelectedFieldData(fieldListTree: Array<TreeNode<TreeItem>>, selectedField: SelectedTreeItem): TreeNode<TreeItem> {
    let selectedItem;
    let selectedItemFound = false;
    const getSelectedItemDetail = (fieldTree: Array<TreeNode<TreeItem>>, field: SelectedTreeItem): TreeNode<TreeItem> => {
      for (const item of fieldTree) {
        if (selectedItemFound) {
          break;
        }
        const hasChildren = item?.children?.length > 0;
        if (hasChildren) {
          getSelectedItemDetail(item.children, field);
        } else if (item.value === field.id && this.isTransIdsMatching(item, field)) {
          selectedItem = item;
          selectedItemFound = true;
        }
      }
      return selectedItem;
    };

    return getSelectedItemDetail(fieldListTree, selectedField);
  }

  public getPathFromReportingFields(reportingFields: Array<TreeNode<TreeItem>>, selectedField: TreeNode<TreeItem>): string {
    return reportingFields.map(reportingField => this.getFieldPath(reportingField, selectedField)).filter(path => !!path)[0].join(', ');
  }

  public fetchTemplates(filter: string, page: number, perPage: number): Observable<{ data: ETemplate[], total: number }> {
    const queryParams = `page=${page}&per-page=${perPage}&order-by=-updateDate&search=${filter}`;
    return this.api.silent.get(`/repository/api/v1/regulation/e-template/list?${queryParams}`, REQUEST_OPTIONS.OBSERVE.RESPONSE)
      .pipe(
        map(response => ({
          total: parseInt(response.headers.get('x-total-count'), 10) || 0,
          data: response.status === 204 ? [] : response.body
        }))
      );
  }

  public getETemplate(id: number): Observable<ETemplate> {
    return this.api.config.silent.get(`/regulation/e-template/${id}`);
  }

  public createNewETemplate(eTemplatePayload: ETemplate, gloRegId: number): Observable<ETemplate> {
    return this.api.config.silent.post(`/regulation/${gloRegId}/e-template/create`, eTemplatePayload);
  }

  public updateETemplate(eTemplate: ETemplate): Observable<Response> {
    return this.api.config.silent.put(`/regulation/${eTemplate.gloRegId}/e-template/update/${eTemplate.id}`, eTemplate);
  }

  public duplicateETemplate(eTemplate: ETemplate, gloRegId: number): Observable<ETemplate> {
    return this.api.config.silent.post(`/regulation/${gloRegId}/e-template/duplicate/${eTemplate.id}`, eTemplate);
  }

  public deleteETemplate(eTemplateId: number) {
    return this.api.config.silent.delete(`/regulation/e-template/delete/${eTemplateId}`);
  }

  public publishETemplate(gloRegId: number, publishRequestData: {
    name: string,
    locale: string
  }): Observable<Response> {
    return this.api.config.silent.post(`/regulation/${gloRegId}/e-template/publish`, publishRequestData);
  }

  public getFolderNames(): Observable<string[]> {
    return this.api.config.get(`/regulation/e-template/folder-names`);
  }

  public generateAiTemplate(request: { name: string, language: string, description: string, existingSfdt?: string }): Observable<AiGenerateApiResponse> {
    return this.api.etemplate.silent.post(`/e-templates/ai`, request);
  }

  public resolveAiTemplateFields(request: { sfdt: string, cliRegId: number }): Observable<ResolvedPlaceholdersResponse> {
    return this.api.etemplate.silent.post(`/e-templates/ai/resolve`, request);
  }

  public saveAiFieldMemory(request: { cliRegId: number, associations: FieldAssociation[] }): Observable<void> {
    return this.api.etemplate.silent.post(`/e-templates/ai/field-memory`, request);
  }

  /**
   * Finds a leaf field by its merge key alone. Unlike getSelectedFieldData this needs no display
   * text, because a resolved field arrives as a field name with no translated label attached.
   */
  public findFieldNodeByName(fieldListTree: Array<TreeNode<TreeItem>>, fieldName: string): TreeNode<TreeItem> {
    for (const node of fieldListTree ?? []) {
      if (node.children?.length > 0) {
        const found = this.findFieldNodeByName(node.children, fieldName);
        if (found) {
          return found;
        }
      } else if (node.value === fieldName) {
        return node;
      }
    }
    return null;
  }

  private getFieldPath(individualReportingField: TreeNode<TreeItem>, selectedField: TreeNode<TreeItem>): string[] | null {
    let selectedItem;
    const getPath = (reportingField: TreeNode<TreeItem>, field: TreeNode<TreeItem>): string[] | null => {
      const hasChildren = reportingField?.children?.length > 0;
      if (hasChildren) {
        for (const child of reportingField.children) {
          const result = getPath(child, field);
          if (result) {
            return reportingField.value ? [reportingField.text, ...result] : result;
          }
        }
      } else if (reportingField.value === field.value && this.isTransIdsMatching(reportingField, field)) {
        selectedItem = [this.translate.instant(reportingField.text)];
      }
      return selectedItem;
    };

    return getPath(individualReportingField, selectedField);
  }

  private isTransIdsMatching(item: TreeNode<TreeItem>, field: SelectedTreeItem | TreeNode<TreeItem>): boolean {
    return (item.data?.transIds?.includes(field.text) ||
      item.data?.transIds?.join(' / ') === field.text ||
      this.translate.instant(item.data.transIds[0]) === field.text);
  }

  private getToken(): Observable<{ Authorization: string }[]> {
    return this.authenticationService.getToken()
      .pipe(
        map(token => [{Authorization: `Bearer ${token}`}]),
        share()
      );
  }
}
